import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, Mail, MapPin, CheckCircle, XCircle, ChevronDown,
  RotateCcw, Clock, HelpCircle, AlertTriangle, Settings, Check, X,
  Link, Scan, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow, differenceInMinutes } from 'date-fns';
import { de } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ── Status-Konfiguration ────────────────────────────────────────────────────
const STATUS_CFG = {
  active:               { label: 'Ausstehend',         badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',     dot: 'bg-blue-500' },
  abgeholt:             { label: 'Abgeholt',            badge: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',  dot: 'bg-green-500' },
  nicht_abgeholt:       { label: 'Nicht abgeholt',      badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',        dot: 'bg-gray-400' },
  storniert:            { label: 'Storniert',            badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',          dot: 'bg-red-500' },
  klarung_noetig:       { label: 'Klärung nötig',        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',  dot: 'bg-amber-500' },
  orphaned_stornierung: { label: 'Waisen-Stornierung',  badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300', dot: 'bg-orange-500' },
};

// ── Zeithelfer ──────────────────────────────────────────────────────────────
function useNow(intervalMs = 5000) {  // 5s statt 30s – damit Button pünktlich aktiv wird
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    // Auch bei Visibility-Change sofort updaten (Android: App aus Hintergrund)
    const onVisible = () => setNow(new Date());
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [intervalMs]);
  return now;
}

function formatDateTime(str) {
  if (!str) return '–';
  try {
    return new Date(str).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return str; }
}

// ── Pickup-Button Logik ─────────────────────────────────────────────────────
function PickupButtonState({ order, isAdmin, strictMode, now }) {
  const startDt = order.pickup_start_dt ? new Date(order.pickup_start_dt) : null;

  // Admin: immer aktiv
  if (isAdmin) return { disabled: false, label: 'Abgeholt', countdown: null };

  // Kein Strict-Mode: immer aktiv
  if (!strictMode) return { disabled: false, label: 'Abgeholt', countdown: null };

  // Strict-Mode: vor Abholzeit
  if (startDt && now < startDt) {
    const diffMin = differenceInMinutes(startDt, now);
    const label = diffMin > 60
      ? `Aktiv in ${Math.floor(diffMin / 60)}h ${diffMin % 60}min`
      : `Aktiv in ${diffMin} min`;
    return { disabled: true, label, countdown: label };
  }

  return { disabled: false, label: 'Abgeholt', countdown: null };
}

// ── Klärungsdialog ──────────────────────────────────────────────────────────
function KlarungDialog({ order, onClose, onClarify }) {
  const [loading, setLoading] = useState(false);

  const handleClarify = async (pickedUp) => {
    setLoading(true);
    await onClarify(order.id, pickedUp);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-amber-500" />
            Klärung nötig
          </DialogTitle>
        </DialogHeader>
        <div className="py-3 space-y-2">
          <p className="text-sm text-muted-foreground">
            Bestellung <span className="font-mono font-medium">{order.bestell_id}</span>
          </p>
          <p className="text-sm text-muted-foreground">
            Abholzeit: <strong>{order.pickup_start_hhmm}–{order.pickup_end_hhmm} Uhr</strong>
          </p>
          <p className="mt-4 font-medium">Wurde die Tüte abgeholt?</p>
        </div>
        <DialogFooter className="flex gap-2 sm:justify-center">
          <Button
            variant="default"
            className="flex-1 gap-2 bg-green-600 hover:bg-green-700 text-white"
            disabled={loading}
            onClick={() => handleClarify(true)}
            data-testid="clarify-yes-btn"
          >
            <Check className="h-4 w-4" /> Ja, abgeholt
          </Button>
          <Button
            variant="outline"
            className="flex-1 gap-2 text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
            disabled={loading}
            onClick={() => handleClarify(false)}
            data-testid="clarify-no-btn"
          >
            <X className="h-4 w-4" /> Nein
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── OrderCard ───────────────────────────────────────────────────────────────
function OrderCard({ order, onMarkRead, onPickup, onResetPickup, onClarify, isAdminOrSL, isAdmin, strictMode, now }) {
  const status   = order.status || 'active';
  const cfg      = STATUS_CFG[status] || STATUS_CFG.active;
  const isNew    = !order.is_read;
  const isAbgeholt = status === 'abgeholt';
  const isKlarung  = status === 'klarung_noetig';

  const btnState = PickupButtonState({ order, isAdmin, strictMode, now });

  return (
    <div
      data-testid={`order-card-${order.id}`}
      className={`rounded-xl border bg-card shadow-sm transition-all ${isNew ? 'border-blue-300 dark:border-blue-700' : 'border-border'} ${isKlarung ? 'border-amber-300 dark:border-amber-600 bg-amber-50/50 dark:bg-amber-950/20' : ''}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between p-3 pb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {isNew && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1" />}
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{order.store_name || 'TGTG Store'}</p>
            <p className="text-xs text-muted-foreground font-mono">{order.bestell_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isKlarung && <AlertTriangle className="h-4 w-4 text-amber-500" />}
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cfg.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Details */}
      <div className="px-3 pb-2 grid grid-cols-2 gap-x-4 gap-y-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3 w-3 flex-shrink-0" />
          <span>{order.pickup_start_hhmm ?? order.abholzeit_start ?? '–'} – {order.pickup_end_hhmm ?? order.abholzeit_end ?? '–'} Uhr</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground justify-end">
          {order.anzahl_unklar || order.anzahl == null ? (
            <span className="text-amber-600 dark:text-amber-400 font-medium" data-testid={`order-qty-${order.id}`}>Menge prüfen</span>
          ) : (
            <span data-testid={`order-qty-${order.id}`}>{order.anzahl}x Tüte</span>
          )}
          <span className="font-medium text-foreground">{(() => {
            if (order.gesamtpreis == null) return '';
            const n = parseFloat(String(order.gesamtpreis).replace(',', '.').replace(/[€\s]/g, ''));
            return isNaN(n) ? order.gesamtpreis : `€${n.toFixed(2)}`;
          })()}</span>
        </div>
        {isAbgeholt && order.abgeholt_at && (
          <div className="col-span-2 text-xs text-green-600 dark:text-green-400">
            Abgeholt um {formatDateTime(order.abgeholt_at)}
          </div>
        )}
        {isKlarung && (
          <div className="col-span-2 text-xs text-amber-700 dark:text-amber-300 font-medium">
            Abholzeit abgelaufen – bitte klären
          </div>
        )}
        {/* Countdown für Strict-Mode */}
        {btnState.countdown && status === 'active' && (
          <div className="col-span-2 flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400">
            <Clock className="h-3 w-3" />
            {btnState.countdown}
          </div>
        )}
      </div>

      {/* Aktionsleiste */}
      <div className="flex items-center justify-between px-3 pb-3 pt-1 border-t border-border gap-2">
        <span className="text-xs text-muted-foreground">{formatDateTime(order.received_at)}</span>
        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
          {/* Abholung bestätigen */}
          {status === 'active' && (
            <Button
              variant="default"
              size="sm"
              onClick={() => onPickup(order.id)}
              disabled={btnState.disabled}
              className={`h-7 text-xs gap-1.5 ${btnState.disabled ? 'bg-gray-200 text-gray-400 dark:bg-gray-700 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'}`}
              data-testid={`pickup-btn-${order.id}`}
              title={btnState.disabled ? btnState.label : 'Abholung bestätigen'}
            >
              <CheckCircle className="h-3 w-3" />
              {btnState.disabled ? <span className="hidden sm:inline">{btnState.label}</span> : 'Abgeholt'}
            </Button>
          )}

          {/* Klärung-Dialog öffnen */}
          {isKlarung && (
            <Button
              variant="default"
              size="sm"
              onClick={() => onClarify(order)}
              className="h-7 text-xs gap-1.5 bg-amber-500 hover:bg-amber-600 text-white"
              data-testid={`clarify-btn-${order.id}`}
            >
              <HelpCircle className="h-3 w-3" />
              Klären
            </Button>
          )}

          {/* Abholung zurücksetzen */}
          {isAbgeholt && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onResetPickup(order.id)}
              className="h-7 text-xs gap-1.5 text-orange-600 border-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20"
              data-testid={`reset-pickup-btn-${order.id}`}
            >
              <RotateCcw className="h-3 w-3" />
              <span className="hidden sm:inline">Zurücksetzen</span>
            </Button>
          )}

          {/* Als gelesen markieren */}
          {isNew && !['storniert', 'abgeholt', 'nicht_abgeholt'].includes(status) && isAdminOrSL && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMarkRead(order.id)}
              className="h-7 text-xs gap-1.5"
              data-testid={`mark-read-btn-${order.id}`}
            >
              <CheckCircle className="h-3 w-3" />
              <span className="hidden sm:inline">Gelesen</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Unbekannte Tokens Dialog ────────────────────────────────────────────────
function UnknownTokenDialog({ unknownTokens, locations, onClose, onLinked }) {
  const [selectedToken, setSelectedToken] = useState(unknownTokens[0]?.token || '');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [linking, setLinking] = useState(false);

  const handleLink = async () => {
    if (!selectedToken || !selectedLocation) return;
    setLinking(true);
    try {
      const res = await axios.post(`${API}/api/admin/tgtg/link-unknown-token`,
        { token: selectedToken, location_id: selectedLocation },
        { headers: authHeader() });
      toast.success(`Token mit "${res.data.location_name}" verknüpft!`);
      onLinked(selectedToken);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Verknüpfen');
    }
    setLinking(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-orange-500" />
            Unbekannter TGTG-Token
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            E-Mails mit folgendem Token konnten keinem Standort zugeordnet werden.
            Bitte verknüpfen Sie den Token mit dem passenden Standort.
          </p>
          {unknownTokens.map(t => (
            <div
              key={t.token}
              onClick={() => setSelectedToken(t.token)}
              className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                selectedToken === t.token
                  ? 'border-orange-400 bg-orange-50 dark:bg-orange-950/20'
                  : 'border-border hover:border-muted-foreground'
              }`}
            >
              <p className="text-sm font-medium font-mono">{t.import_email}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t.count}x gesehen · Letzte E-Mail: „{t.last_email_subject?.slice(0, 50)}"
              </p>
            </div>
          ))}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Token verknüpfen mit Standort:</label>
            <select
              value={selectedLocation}
              onChange={e => setSelectedLocation(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="token-location-select"
            >
              <option value="">– Standort auswählen –</option>
              {locations.map(loc => (
                <option key={loc.location_id} value={loc.location_id}>{loc.name}</option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button
            onClick={handleLink}
            disabled={!selectedLocation || !selectedToken || linking}
            className="gap-2 bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="link-token-btn"
          >
            <Link className="h-4 w-4" />
            {linking ? 'Verknüpfe…' : 'Verknüpfen & Rescan starten'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Standort-Einstellungen Panel ────────────────────────────────────────────
function LocationSettingsPanel({ onClose, onRescan }) {
  const [settings, setSettings] = useState([]);
  const [saving, setSaving] = useState(null);
  const [rescanning, setRescanning] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/tgtg/location-settings`, { headers: authHeader() })
      .then(r => setSettings(r.data))
      .catch(() => toast.error('Einstellungen konnten nicht geladen werden'));
  }, []);

  const toggle = async (locationId, currentVal) => {
    setSaving(locationId);
    try {
      await axios.put(`${API}/api/admin/tgtg/location-settings`, {
        location_id: locationId,
        tgtg_strict_pickup_time: !currentVal
      }, { headers: authHeader() });
      setSettings(prev => prev.map(s =>
        s.location_id === locationId ? { ...s, tgtg_strict_pickup_time: !currentVal } : s
      ));
      toast.success('Einstellung gespeichert');
    } catch {
      toast.error('Fehler beim Speichern');
    }
    setSaving(null);
  };

  const handleRescan = async () => {
    setRescanning(true);
    try {
      const res = await axios.post(`${API}/api/admin/tgtg/rescan-48h`, {}, { headers: authHeader() });
      toast.success(res.data.message || 'Rescan gestartet');
      if (onRescan) setTimeout(onRescan, 3000); // Nach 3s Bestellliste neu laden
    } catch {
      toast.error('Fehler beim Starten des Rescans');
    }
    setRescanning(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Settings className="h-4 w-4" /> TGTG-Einstellungen pro Standort
        </h3>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-3">
        {settings.map(s => (
          <div key={s.location_id} className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">{s.name}</p>
              <p className="text-xs text-muted-foreground">
                {s.tgtg_strict_pickup_time
                  ? 'Strict: Button erst ab Abholzeit aktiv'
                  : 'Flexibel: Button immer aktiv'}
              </p>
            </div>
            <button
              onClick={() => toggle(s.location_id, s.tgtg_strict_pickup_time)}
              disabled={saving === s.location_id}
              data-testid={`strict-toggle-${s.location_id}`}
              className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none ${
                s.tgtg_strict_pickup_time ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                s.tgtg_strict_pickup_time ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
        ))}
        <p className="text-xs text-muted-foreground pt-1 border-t border-border">
          <strong>Strict-Modus:</strong> Mitarbeiter können den "Abgeholt"-Button erst ab der Abholzeit klicken.
          Admins können immer klicken (Override).
        </p>
        {/* Rescan-Button */}
        <div className="pt-1 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRescan}
            disabled={rescanning}
            className="w-full gap-2 text-xs"
            data-testid="rescan-48h-btn"
          >
            <Scan className={`h-3.5 w-3.5 ${rescanning ? 'animate-spin' : ''}`} />
            {rescanning ? 'Rescan läuft…' : 'Verpasste E-Mails neu importieren (letzte 48h)'}
          </Button>
          <p className="text-xs text-muted-foreground mt-1.5">
            Importiert TGTG-Bestellungen der letzten 48 Stunden erneut.
            Nützlich nach Token-Verknüpfung oder bei fehlenden Bestellungen.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Hauptkomponente ─────────────────────────────────────────────────────────
export default function TGTGOrders() {
  const { user } = useAuth();
  const [orders, setOrders]                   = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [locationEmails, setLocationEmails]   = useState([]);
  const [showImportInfo, setShowImportInfo]   = useState(false);
  const [locationSettings, setLocationSettings] = useState({});
  const [showSettings, setShowSettings]       = useState(false);
  const [klarungOrder, setKlarungOrder]       = useState(null);
  const [activeFilter, setActiveFilter]       = useState('all');
  const [unknownTokens, setUnknownTokens]     = useState([]);
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const now = useNow(30000);

  const isAdminOrSL = ['admin', 'superadmin', 'stationsleitung'].includes(user?.role);
  const isAdmin     = ['admin', 'superadmin'].includes(user?.role);

  // Strikte Modus-Prüfung für eine Bestellung
  const isStrictMode = useCallback((order) => {
    const locId = order.location_id;
    return locationSettings[locId] ?? false;
  }, [locationSettings]);

  // Daten laden
  const fetchOrders = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [ordersRes, emailsRes] = await Promise.all([
        axios.get(`${API}/api/admin/tgtg/orders`, { headers: authHeader() }),
        axios.get(`${API}/api/admin/tgtg/locations-emails`, { headers: authHeader() }).catch(() => ({ data: { locations: [] } })),
      ]);
      setOrders(ordersRes.data);
      setLocationEmails(emailsRes.data.locations || []);
    } catch {
      toast.error('Bestellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  // Unbekannte Tokens laden (nur Admins)
  const fetchUnknownTokens = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/api/admin/tgtg/unknown-tokens`, { headers: authHeader() });
      setUnknownTokens(res.data || []);
    } catch { /* silent */ }
  }, [isAdmin]);

  // Standort-Einstellungen laden
  const fetchLocationSettings = useCallback(async () => {
    if (!isAdminOrSL) return;
    try {
      const res = await axios.get(`${API}/api/admin/tgtg/location-settings`, { headers: authHeader() });
      const map = {};
      res.data.forEach(s => { map[s.location_id] = s.tgtg_strict_pickup_time; });
      setLocationSettings(map);
    } catch { /* silent */ }
  }, [isAdminOrSL]);

  // Beim Laden: abgelaufene Bestellungen prüfen
  const checkExpired = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/api/admin/tgtg/check-expired`, {}, { headers: authHeader() });
      if (res.data.updated > 0) {
        await fetchOrders(true); // Neu laden falls Änderungen
      }
    } catch { /* silent */ }
  }, [fetchOrders]);

  useEffect(() => {
    Promise.all([fetchOrders(), fetchLocationSettings(), fetchUnknownTokens()])
      .then(() => checkExpired());
  }, [fetchOrders, fetchLocationSettings, fetchUnknownTokens, checkExpired]);

  // Aktionen
  const handleMarkRead = async (orderId) => {
    try {
      await axios.put(`${API}/api/admin/tgtg/orders/${orderId}/read`, {}, { headers: authHeader() });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, is_read: true } : o));
    } catch { toast.error('Fehler'); }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/api/admin/tgtg/orders/mark-all-read`, {}, { headers: authHeader() });
      setOrders(prev => prev.map(o => ({ ...o, is_read: true })));
      toast.success('Alle als gelesen markiert');
    } catch { toast.error('Fehler'); }
  };

  const handlePickup = async (orderId) => {
    try {
      await axios.put(`${API}/api/admin/tgtg/orders/${orderId}/pickup`, {}, { headers: authHeader() });
      setOrders(prev => prev.map(o =>
        o.id === orderId ? { ...o, status: 'abgeholt', is_read: true, abgeholt_at: new Date().toISOString() } : o
      ));
      toast.success('Abholung bestätigt!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Fehler beim Bestätigen';
      toast.error(msg);
    }
  };

  const handleResetPickup = async (orderId) => {
    try {
      await axios.put(`${API}/api/admin/tgtg/orders/${orderId}/reset-pickup`, {}, { headers: authHeader() });
      setOrders(prev => prev.map(o =>
        o.id === orderId ? { ...o, status: 'active', abgeholt_at: null } : o
      ));
      toast.success('Abholung zurückgesetzt');
    } catch { toast.error('Fehler beim Zurücksetzen'); }
  };

  const handleClarify = async (orderId, pickedUp) => {
    try {
      const res = await axios.put(`${API}/api/admin/tgtg/orders/${orderId}/clarify`,
        { picked_up: pickedUp }, { headers: authHeader() });
      setOrders(prev => prev.map(o =>
        o.id === orderId ? { ...o, status: res.data.new_status, abgeholt_at: pickedUp ? new Date().toISOString() : null } : o
      ));
      toast.success(pickedUp ? 'Als abgeholt markiert' : 'Als nicht abgeholt markiert');
    } catch { toast.error('Fehler bei der Klärung'); }
  };

  // Filter
  const filteredOrders = orders.filter(o => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'open') return ['active', 'klarung_noetig'].includes(o.status);
    return o.status === activeFilter;
  });

  const klarungCount = orders.filter(o => o.status === 'klarung_noetig').length;
  const unreadCount  = orders.filter(o => !o.is_read).length;

  const FILTERS = [
    { key: 'all',           label: 'Alle' },
    { key: 'open',          label: 'Offen', badge: orders.filter(o => ['active','klarung_noetig'].includes(o.status)).length },
    { key: 'active',        label: 'Ausstehend' },
    { key: 'klarung_noetig',label: 'Klärung', badge: klarungCount, badgeColor: 'bg-amber-500' },
    { key: 'abgeholt',      label: 'Abgeholt' },
    { key: 'nicht_abgeholt',label: 'Nicht abgeholt' },
    { key: 'storniert',     label: 'Storniert' },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">Too Good To Go</h1>
            <p className="text-sm text-muted-foreground">
              {orders.length} Bestellungen
              {klarungCount > 0 && <span className="ml-2 text-amber-600 font-medium">· {klarungCount} Klärung nötig</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdminOrSL && (
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(v => !v)}
                data-testid="tgtg-settings-btn" className="h-8 gap-1.5">
                <Settings className="h-3.5 w-3.5" /> Einstellungen
              </Button>
            )}
            {isAdminOrSL && unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleMarkAllRead}
                data-testid="mark-all-read-btn" className="h-8 gap-1.5">
                <CheckCircle className="h-3.5 w-3.5" /> Alle gelesen
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => fetchOrders()}
              data-testid="refresh-btn" className="h-8 gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Import-Email Info */}
        {isAdminOrSL && (
          <div className="rounded-xl border border-border bg-muted/50 p-3">
            <button
              className="flex items-center justify-between w-full text-left"
              onClick={() => setShowImportInfo(v => !v)}
              data-testid="import-info-toggle"
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <Mail className="h-4 w-4 text-green-500" />
                TGTG Import-E-Mails
              </div>
              <ChevronDown className={`h-4 w-4 transition-transform ${showImportInfo ? 'rotate-180' : ''}`} />
            </button>
            {showImportInfo && (
              <div className="mt-3 space-y-2">
                {locationEmails.map(loc => (
                  <div key={loc.location_id} className="text-xs space-y-1">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <MapPin className="h-3 w-3" /> {loc.name}
                    </div>
                    <div
                      className="font-mono bg-background rounded px-2 py-1 border cursor-pointer hover:bg-muted"
                      onClick={() => { navigator.clipboard?.writeText(loc.import_email); toast.success('Kopiert!'); }}
                      data-testid={`import-email-${loc.location_id}`}
                    >
                      {loc.import_email}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Standort-Einstellungen Panel */}
        {showSettings && isAdminOrSL && (
          <LocationSettingsPanel
            onClose={() => setShowSettings(false)}
            onRescan={() => fetchOrders(true)}
          />
        )}

        {/* Klärung nötig – Hinweis-Banner */}
        {klarungCount > 0 && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 flex items-center gap-3"
            data-testid="klarung-banner">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-amber-800 dark:text-amber-300">{klarungCount} Bestellung{klarungCount > 1 ? 'en' : ''} – Klärung nötig</p>
              <p className="text-amber-700 dark:text-amber-400 text-xs">Abholzeit abgelaufen ohne Bestätigung</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveFilter('klarung_noetig')}
              className="ml-auto h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
              data-testid="filter-klarung-btn"
            >
              Anzeigen
            </Button>
          </div>
        )}

        {/* Unbekannte Token-Banner */}
        {isAdmin && unknownTokens.length > 0 && (
          <div className="rounded-xl border border-orange-300 bg-orange-50 dark:bg-orange-950/30 p-3 flex items-start gap-3"
            data-testid="unknown-token-banner">
            <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm flex-1 min-w-0">
              <p className="font-semibold text-orange-800 dark:text-orange-300">
                {unknownTokens.length} unbekannte TGTG-E-Mail{unknownTokens.length > 1 ? 's' : ''}
              </p>
              <p className="text-orange-700 dark:text-orange-400 text-xs truncate">
                Token: {unknownTokens.map(t => t.token).join(', ')} – Standort nicht zugeordnet
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTokenDialog(true)}
              className="flex-shrink-0 h-7 text-xs border-orange-400 text-orange-700 hover:bg-orange-100"
              data-testid="fix-token-btn"
            >
              <Link className="h-3 w-3 mr-1" />
              Zuordnen
            </Button>
          </div>
        )}

        {/* Filter-Tabs */}
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors relative ${
                activeFilter === f.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {f.label}
              {f.badge > 0 && (
                <span className={`ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[10px] ${f.badgeColor || 'bg-blue-500'}`}>
                  {f.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Bestellliste */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
            Bestellungen werden geladen…
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <XCircle className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p className="font-medium">Keine Bestellungen vorhanden</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                onMarkRead={handleMarkRead}
                onPickup={handlePickup}
                onResetPickup={handleResetPickup}
                onClarify={(o) => setKlarungOrder(o)}
                isAdminOrSL={isAdminOrSL}
                isAdmin={isAdmin}
                strictMode={isStrictMode(order)}
                now={now}
              />
            ))}
          </div>
        )}
      </div>

      {/* Klärungsdialog */}
      {klarungOrder && (
        <KlarungDialog
          order={klarungOrder}
          onClose={() => setKlarungOrder(null)}
          onClarify={handleClarify}
        />
      )}

      {/* Unbekannter Token Dialog */}
      {showTokenDialog && unknownTokens.length > 0 && (
        <UnknownTokenDialog
          unknownTokens={unknownTokens}
          locations={locationEmails}
          onClose={() => setShowTokenDialog(false)}
          onLinked={(token) => {
            setUnknownTokens(prev => prev.filter(t => t.token !== token));
            // After linking, auto-rescan
            axios.post(`${API}/api/admin/tgtg/rescan-48h`, {}, { headers: authHeader() })
              .then(() => {
                toast.info('Rescan gestartet – Bestellungen werden in Kürze importiert');
                setTimeout(() => fetchOrders(true), 5000);
              })
              .catch(() => {});
          }}
        />
      )}
    </DashboardLayout>
  );
}
